import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import App from '../App';
import { TimelineProvider } from '../features/timeline/context/TimelineContext';
import ErrorBoundary from '../shared/components/ErrorBoundary';
// Static pages
import TermsPage from '../pages/TermsPage';
import PrivacyPage from '../pages/PrivacyPage';
import AboutPage from '../pages/AboutPage';
// Auth pages
import AuthCallbackPage from '../features/auth/pages/AuthCallbackPage';
import AccountSettingsPage from '../features/auth/pages/AccountSettingsPage';
import TVAuthPage from '../features/auth/pages/TVAuthPage';
// Admin pages
import AdminDashboardPage from '../features/admin/pages/AdminDashboardPage';
import AdminUsersPage from '../features/admin/pages/AdminUsersPage';
import AdminUserDetailPage from '../features/admin/pages/AdminUserDetailPage';
import AdminTimelinesPage from '../features/admin/pages/AdminTimelinesPage';
import AdminNotificationsPage from '../features/admin/pages/AdminNotificationsPage';
import AdminSystemPage from '../features/admin/pages/AdminSystemPage';

/**
 * App wrapped with TimelineProvider and ErrorBoundary
 * TimelineProvider needs router context for useParams and useNavigate
 */
const AppWithTimeline: React.FC = () => (
  <ErrorBoundary>
    <TimelineProvider>
      <App />
    </TimelineProvider>
  </ErrorBoundary>
);

/**
 * Main router component for the application
 * Uses BrowserRouter for clean URLs on custom domain
 * Both / and /t/:code routes now use App for unified experience
 */
const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public timeline route - now uses main App for full experience */}
        <Route path="/t/:code" element={<AppWithTimeline />} />

        {/* Auth callback routes */}
        <Route path="/auth/verify-email" element={<AuthCallbackPage type="verify-email" />} />
        <Route path="/auth/magic-link" element={<AuthCallbackPage type="magic-link" />} />

        {/* Static pages */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/about" element={<AboutPage />} />

        {/* Account settings */}
        <Route path="/account" element={<AccountSettingsPage />} />

        {/* TV authentication (device code flow) */}
        <Route path="/tv" element={<TVAuthPage />} />

        {/* Admin routes */}
        <Route path="/admin" element={<AdminDashboardPage />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/users/:id" element={<AdminUserDetailPage />} />
        <Route path="/admin/timelines" element={<AdminTimelinesPage />} />
        <Route path="/admin/notifications" element={<AdminNotificationsPage />} />
        <Route path="/admin/system" element={<AdminSystemPage />} />

        {/* Main app route (default) */}
        <Route path="/" element={<AppWithTimeline />} />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRouter;
